import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const CartItem = ({ item, onUpdateQuantity, onRemoveItem, onMoveToWishlist }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleQuantityChange = async (newQuantity) => {
    if (newQuantity < 1 || newQuantity === item?.quantity) return;
    
    setIsUpdating(true);
    try {
      onUpdateQuantity(item?.id, newQuantity);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemove = () => {
    onRemoveItem(item?.id);
  };

  const handleMoveToWishlist = () => {
    onMoveToWishlist(item?.id);
  };

  const itemTotal = item?.price * item?.quantity;
  const originalTotal = item?.originalPrice ? item?.originalPrice * item?.quantity : null;
  const savings = originalTotal ? originalTotal - itemTotal : 0;

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Product Image */}
        <div className="flex-shrink-0">
          <div className="w-24 h-24 sm:w-32 sm:h-32 bg-muted rounded-lg overflow-hidden">
            <Image
              src={item?.image}
              alt={item?.imageAlt}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-heading font-medium text-foreground mb-1">
                {item?.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                {item?.brand}
              </p>
              
              {/* Variant Information */}
              {item?.variant && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-flex items-center px-2 py-1 bg-muted rounded-md text-xs font-medium text-muted-foreground">
                    {item?.variant?.type}: {item?.variant?.value}
                  </span>
                </div>
              )}

              {/* Stock Status */}
              <div className="flex items-center gap-2 mb-3">
                {item?.inStock ? (
                  <div className="flex items-center gap-1">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-success">In Stock</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Icon name="AlertCircle" size={16} className="text-error" />
                    <span className="text-sm text-error">Out of Stock</span>
                  </div>
                )}
                {item?.stockLevel && item?.stockLevel <= 5 && item?.inStock && (
                  <span className="text-xs text-warning">
                    Only {item?.stockLevel} left
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMoveToWishlist}
                  iconName="Heart"
                  iconPosition="left"
                >
                  Save for Later
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemove}
                  iconName="Trash2"
                  iconPosition="left"
                >
                  Remove
                </Button>
              </div>
            </div>

            {/* Price and Quantity */}
            <div className="flex flex-col items-end gap-4">
              {/* Price */}
              <div className="text-right">
                <div className="text-xl font-semibold text-foreground">
                  ${itemTotal?.toFixed(2)}
                </div>
                {originalTotal && (
                  <div className="text-sm text-muted-foreground line-through">
                    ${originalTotal?.toFixed(2)}
                  </div>
                )}
                {savings > 0 && (
                  <div className="text-sm text-success">
                    Save ${savings?.toFixed(2)}
                  </div>
                )}
                <div className="text-sm text-muted-foreground">
                  ${item?.price?.toFixed(2)} each
                </div>
              </div>

              {/* Quantity Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleQuantityChange(item?.quantity - 1)}
                  disabled={item?.quantity <= 1 || isUpdating || !item?.inStock}
                  className="w-8 h-8"
                >
                  <Icon name="Minus" size={14} />
                </Button>
                
                <div className="flex items-center justify-center min-w-[3rem] h-8 px-2 border border-border rounded-md bg-background">
                  {isUpdating ? (
                    <Icon name="Loader2" size={14} className="animate-spin" />
                  ) : (
                    <span className="text-sm font-medium">{item?.quantity}</span>
                  )}
                </div>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleQuantityChange(item?.quantity + 1)}
                  disabled={isUpdating || !item?.inStock || (item?.stockLevel && item?.quantity >= item?.stockLevel)}
                  className="w-8 h-8"
                >
                  <Icon name="Plus" size={14} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;